import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Collapse, Stack, Tab, Tabs, Typography, alpha, useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Code as WriteItIcon,
  Functions as ExplainItIcon,
  CompareArrows as CompareItIcon,
  Work as UseItIcon,
  BarChart as SeeItIcon,
  KeyboardArrowRight as NextIcon,
  KeyboardArrowLeft as PrevIcon,
  Lightbulb as HintIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import { getFiveHowConfig, isStepAnswered, STEP_TYPES } from './config/workspaceTypes';
import StepPanel from './shared/StepPanel';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';

// ── Lens metadata ─────────────────────────────────────────────────────────────
// No per-lens hues. Colour is supplied at render time from the MUI theme so the
// palette stays coherent across light and dark modes.

const FIVE_HOW_META = [
  {
    key: 'practical',
    label: 'Write it',
    subtitle: 'Solve with real code',
    workedLabel: 'Platform solution',
    icon: WriteItIcon,
    instruction: 'Use VS Code/GitHub for the full project proof. Use this browser runner only for a tiny concept test or key snippet.',
  },
  {
    key: 'logic',
    label: 'Explain it',
    subtitle: 'Step-by-step pseudocode',
    workedLabel: 'Platform pseudocode',
    icon: ExplainItIcon,
    instruction: 'Explain the same idea in plain steps so you can reason about it without memorising code.',
  },
  {
    key: 'analogy',
    label: 'Compare it',
    subtitle: 'Real-life parallel',
    workedLabel: 'Platform analogy',
    icon: CompareItIcon,
    instruction: 'Create a real-life comparison so the concept sticks outside the tool or syntax.',
  },
  {
    key: 'story',
    label: 'Use it',
    subtitle: 'Apply in a real job',
    workedLabel: 'Platform job story',
    icon: UseItIcon,
    instruction: 'Connect the concept to a job situation so the learner can explain why it matters at work.',
  },
  {
    key: 'visual',
    label: 'See it',
    subtitle: 'Show the output',
    workedLabel: 'Platform output',
    icon: SeeItIcon,
    instruction: 'Show the visible result or evidence, not another full solution.',
  },
];

// ── Scenario card ─────────────────────────────────────────────────────────────

const ScenarioCard = ({ prompt }) => (
  <Box sx={{
    p: 1.5,
    borderRadius: 1,
    borderLeft: '3px solid',
    borderLeftColor: 'divider',
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'action.hover',
  }}>
    <Typography
      variant="caption"
      fontWeight={700}
      sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.75 }}
    >
      Scenario reference
    </Typography>
    <Typography variant="caption" sx={{ lineHeight: 1.65, color: 'text.secondary', display: 'block' }}>
      {prompt}
    </Typography>
  </Box>
);

// ── Worked example ────────────────────────────────────────────────────────────

const WorkedExample = ({ explanation, title = 'Platform example' }) => {
  const isLongExample = String(explanation || '').length > 700;
  const [open, setOpen] = useState(!isLongExample);
  return (
    <Box sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      <Box
        onClick={() => setOpen(v => !v)}
        sx={{
          px: 2, py: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          bgcolor: 'action.selected',
          cursor: 'pointer', userSelect: 'none',
          borderBottom: open ? '1px solid' : 'none',
          borderBottomColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label="Platform"
            size="small"
            variant="outlined"
            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.8 }}
          />
          <Typography variant="caption" fontWeight={700} sx={{ color: 'text.primary', letterSpacing: 0.3 }}>
            {title} — use only if you need a model
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, flexShrink: 0 }}>
          {open ? '▲ hide' : '▼ show'}
        </Typography>
      </Box>
      <Collapse in={open}>
        <Box sx={{ p: 2.5 }}>
          <MarkdownRenderer content={explanation} />
        </Box>
      </Collapse>
    </Box>
  );
};

// ── "Your turn" divider ───────────────────────────────────────────────────────

const YourTurnDivider = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 1 }}>
    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
    <Typography
      variant="caption"
      fontWeight={700}
      color="text.secondary"
      sx={{ textTransform: 'uppercase', letterSpacing: 1.2, whiteSpace: 'nowrap', fontSize: '0.65rem' }}
    >
      Your turn — now apply it
    </Typography>
    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
  </Box>
);

// ── Practice problems ─────────────────────────────────────────────────────────

const PracticeProblems = ({ problems, taskType, responses, onResponseChange, onComplete, saveStatus }) => {
  const [index, setIndex] = useState(0);
  const [showHint, setShowHint] = useState(false);

  const problem = problems[index];
  const problemKey = `similar_${index + 1}`;
  const currentResponse = responses[problemKey] || {};

  const stepConfig = {
    type: STEP_TYPES.CODE_RUNNER,
    language: problem.language || (taskType === 'sql' ? 'duckdb' : 'python'),
    allowedLanguages: problem.language === 'excel' ? ['excel'] : undefined,
    placeholder: problem.starterCode || '',
  };

  const goTo = (i) => { setIndex(i); setShowHint(false); };

  return (
    <Stack spacing={0}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1.5 }}>
          Practice scenario {index + 1} of {problems.length}
        </Typography>
        <Typography variant="h6" fontWeight={700} mt={0.5} mb={0.25}>
          {problem.prompt}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Apply what you learned — write the code from scratch, no hints unless you're stuck.
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
        <StepPanel
          stepConfig={stepConfig}
          itemContext={{ task_type: taskType }}
          response={currentResponse}
          onChange={(value) => onResponseChange(problemKey, value)}
        />
      </Box>

      {problem.hint && (
        <Box sx={{ px: { xs: 2, md: 3 }, pb: 1 }}>
          <Button size="small" startIcon={<HintIcon fontSize="small" />} onClick={() => setShowHint(v => !v)}>
            {showHint ? 'Hide solution' : 'Stuck? Show solution'}
          </Button>
          <Collapse in={showHint}>
            <Box sx={{ mt: 1, p: 2, bgcolor: 'action.hover', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <MarkdownRenderer content={problem.hint} />
            </Box>
          </Collapse>
        </Box>
      )}

      <Box sx={{
        px: { xs: 2, md: 3 }, py: 2,
        borderTop: 1, borderColor: 'divider',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <Button variant="outlined" size="small" startIcon={<PrevIcon />} disabled={index === 0} onClick={() => goTo(index - 1)}>
          Previous
        </Button>
        <Typography variant="caption" color="text.secondary">
          {saveStatus === 'saving' && 'Saving…'}
          {saveStatus === 'saved' && 'Saved ✓'}
          {saveStatus === 'error' && 'Save failed'}
        </Typography>
        {index < problems.length - 1 ? (
          <Button variant="contained" size="small" endIcon={<NextIcon />} onClick={() => goTo(index + 1)}>
            Next scenario
          </Button>
        ) : (
          <Button variant="contained" color="success" size="small" onClick={onComplete}>
            Complete ✓
          </Button>
        )}
      </Box>
    </Stack>
  );
};

// ── Main FiveHowWorkspace ─────────────────────────────────────────────────────

const FiveHowWorkspace = ({
  item,
  responses,
  onResponseChange,
  disabled,
  onComplete,
  saveStatus,
}) => {
  const theme = useTheme();
  const [stage, setStage] = useState('learn');
  const [modeIndex, setModeIndex] = useState(0);

  const taskType = item?.context?.task_type || 'generic';
  const fiveHowConfig = useMemo(() => getFiveHowConfig(taskType), [taskType]);
  const fiveHowContent = item?.fiveHowContent || {};
  const similarProblems = item?.similarProblems || [];
  const hasPractice = similarProblems.length > 0;

  const currentMeta = FIVE_HOW_META[modeIndex];
  const currentConfig = fiveHowConfig[currentMeta?.key];
  const currentResponse = responses[currentMeta?.key] || {};
  const itemContext = item?.context || {};
  const lensContent = fiveHowContent[currentMeta?.key];

  const answeredCount = FIVE_HOW_META.filter((m) => isStepAnswered(responses[m.key])).length;

  // Single accent drawn from the theme — used only for the active tab indicator
  const accent = theme.palette.primary.main;

  if (stage === 'practice') {
    return (
      <PracticeProblems
        problems={similarProblems}
        taskType={taskType}
        responses={responses}
        onResponseChange={onResponseChange}
        onComplete={onComplete}
        saveStatus={saveStatus}
      />
    );
  }

  if (!currentConfig) {
    return <Alert severity="error" sx={{ m: 3 }}>No 5-HOW config found for task type "{taskType}".</Alert>;
  }

  return (
    <Stack spacing={0}>
      {/* ── Lens tabs ──────────────────────────────────────────────────── */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 0, md: 1 } }}>
        <Tabs
          value={modeIndex}
          onChange={(_, v) => typeof v === 'number' && setModeIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ style: { backgroundColor: accent } }}
        >
          {FIVE_HOW_META.map((meta, i) => {
            const answered = isStepAnswered(responses[meta.key]);
            const active = modeIndex === i;
            const Icon = meta.icon;
            return (
              <Tab
                key={meta.key}
                value={i}
                sx={{ minHeight: 58, py: 1, px: { xs: 1.5, md: 2 } }}
                label={
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                      bgcolor: active ? alpha(accent, 0.12) : 'transparent',
                      color: active ? 'primary.main' : answered ? 'success.main' : 'text.disabled',
                      transition: 'all 0.15s',
                    }}>
                      {answered ? <CheckIcon sx={{ fontSize: 14 }} /> : <Icon sx={{ fontSize: 14 }} />}
                    </Box>
                    <Stack alignItems="flex-start" spacing={0}>
                      <Typography
                        variant="caption"
                        fontWeight={active ? 700 : 500}
                        sx={{ lineHeight: 1.2, fontSize: '0.72rem', color: active ? 'primary.main' : 'text.primary' }}
                      >
                        {meta.label}
                      </Typography>
                      <Typography variant="caption" sx={{ lineHeight: 1.1, fontSize: '0.6rem', color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
                        {meta.subtitle}
                      </Typography>
                    </Stack>
                  </Stack>
                }
              />
            );
          })}

          {/* "Try it" practice tab */}
          {hasPractice && (
            <Tab
              value={5}
              onClick={() => setStage('practice')}
              sx={{ minHeight: 58, py: 1, px: { xs: 1.5, md: 2 } }}
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                    color: 'text.disabled',
                  }}>
                    <PlayIcon sx={{ fontSize: 14 }} />
                  </Box>
                  <Stack alignItems="flex-start" spacing={0}>
                    <Typography variant="caption" fontWeight={500} sx={{ lineHeight: 1.2, fontSize: '0.72rem' }}>Try it</Typography>
                    <Typography variant="caption" sx={{ lineHeight: 1.1, fontSize: '0.6rem', color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
                      {similarProblems.length} scenario{similarProblems.length !== 1 ? 's' : ''}
                    </Typography>
                  </Stack>
                </Stack>
              }
            />
          )}
        </Tabs>
      </Box>

      {/* ── Lens header ────────────────────────────────────────────────── */}
      <Box sx={{ px: { xs: 2, md: 3 }, pt: 2, pb: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1.5, lineHeight: 1 }}>
              {currentMeta.label}
            </Typography>
            <Typography variant="h6" fontWeight={800} mt={0.25} mb={0.5}>
              {currentMeta.subtitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
              {currentMeta.instruction}
            </Typography>
          </Box>
          <Chip
            label={`${answeredCount} / 5`}
            size="small"
            color={answeredCount === 5 ? 'success' : 'default'}
            variant={answeredCount === 5 ? 'filled' : 'outlined'}
            sx={{ flexShrink: 0, mt: 0.5 }}
          />
        </Stack>
      </Box>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <Stack spacing={2} sx={{ px: { xs: 2, md: 3 }, pb: 2 }}>

        {item?.prompt && <ScenarioCard prompt={item.prompt} />}

        {lensContent?.explanation && (
          <WorkedExample
            explanation={lensContent.explanation}
            title={currentMeta.workedLabel || 'Platform example'}
          />
        )}

        <YourTurnDivider />

        <Alert severity="info" icon={false} sx={{ py: 0.75 }}>
          <Typography variant="caption" color="text.secondary">
            Use backticks for inline code in written answers, for example `files = sorted(glob.glob("data/*.csv"))`.
            Code-runner answers are saved as code blocks automatically.
          </Typography>
        </Alert>

        <StepPanel
          stepConfig={currentConfig}
          itemContext={itemContext}
          response={currentResponse}
          onChange={(value) => onResponseChange(currentMeta.key, value)}
          disabled={disabled}
        />
      </Stack>

      {/* ── Navigation ─────────────────────────────────────────────────── */}
      <Box sx={{
        px: { xs: 2, md: 3 }, py: 2,
        borderTop: 1, borderColor: 'divider',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
      }}>
        <Button
          variant="outlined" size="small" startIcon={<PrevIcon />}
          disabled={modeIndex === 0}
          onClick={() => setModeIndex(i => i - 1)}
        >
          Previous
        </Button>

        <Typography variant="caption" color="text.secondary">
          {saveStatus === 'saving' && 'Saving…'}
          {saveStatus === 'saved' && 'Saved ✓'}
          {saveStatus === 'error' && 'Save failed'}
        </Typography>

        {modeIndex < FIVE_HOW_META.length - 1 ? (
          <Button variant="contained" size="small" endIcon={<NextIcon />}
            onClick={() => setModeIndex(i => i + 1)}>
            Next
          </Button>
        ) : hasPractice ? (
          <Button variant="contained" size="large" color="primary" endIcon={<PlayIcon />}
            onClick={() => setStage('practice')}
            sx={{ fontWeight: 700 }}>
            Try the practice scenarios →
          </Button>
        ) : (
          <Button variant="contained" color="success" size="small" onClick={onComplete}>
            Complete ✓
          </Button>
        )}
      </Box>
    </Stack>
  );
};

export default FiveHowWorkspace;
